import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, ChevronDown, MessageCircle, Globe, ArrowRight } from "lucide-react";
import Logo from "../Logo";
import { useI18n } from "../../i18n/LanguageProvider";
import { useAlternates } from "./Layout";
import { SITE, WHATSAPP_LINK, LANG_LABELS } from "../../config/site";
import { getTreatment } from "../../data/treatments";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetClose,
  SheetTitle,
} from "../ui/sheet";

export default function Header() {
  const { t, lang, p, e } = useI18n();
  const { alternates } = useAlternates();
  const navigate = useNavigate();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const implants = getTreatment("dental-implants");
  const navItems = [
    { label: t.nav.treatments, to: p("dentalTreatment") },
    { label: t.nav.implants, to: e(implants.slug) },
    { label: t.nav.prices, to: p("prices") },
    { label: t.nav.clinics, to: p("clinics") },
    { label: t.nav.locations, to: p("dentalTourism") },
    { label: t.nav.beforeAfter, to: p("beforeAfter") },
    { label: t.nav.patientGuide, to: p("patientGuide") },
    { label: t.nav.blog, to: p("blog") },
    { label: t.nav.faq, to: p("faq") },
    { label: t.nav.contact, to: p("contact") },
  ];

  const handleLangSwitch = (targetLang) => {
    if (alternates && alternates[targetLang]) {
      navigate(alternates[targetLang]);
    } else {
      navigate(targetLang === "en" ? "/" : `/${targetLang}`);
    }
  };

  const isActive = (to) => location.pathname === to;

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-50 w-full transition-all duration-300 ${
        scrolled
          ? "bg-white/90 backdrop-blur-xl border-b border-navy/10 shadow-[0_4px_20px_rgb(10_25_47/0.04)]"
          : "bg-white/80 backdrop-blur-md border-b border-transparent"
      }`}
    >
      <div className="container-dv flex h-20 items-center justify-between gap-4">
        <Logo />

        {/* Desktop nav */}
        <nav
          className="hidden xl:flex items-center gap-1"
          aria-label="Primary"
          data-testid="desktop-nav"
        >
          {navItems.map((item) => (
            <Link
              key={item.to + item.label}
              to={item.to}
              data-testid={`nav-link-${item.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className={`px-3 py-2 text-[13px] font-semibold rounded-full transition-colors whitespace-nowrap ${
                isActive(item.to)
                  ? "text-dental bg-dental/5"
                  : "text-navy/75 hover:text-dental hover:bg-mist"
              }`}
            >
              {item.label}
            </Link>
          ))}
        </nav>

        {/* Right actions */}
        <div className="flex items-center gap-2">
          {/* Language selector */}
          <DropdownMenu>
            <DropdownMenuTrigger
              data-testid="language-selector"
              className="inline-flex items-center gap-1.5 rounded-full border border-navy/15 px-3 py-2 text-sm font-semibold text-navy hover:border-dental/40 hover:text-dental transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-dental"
              aria-label="Select language"
            >
              <Globe className="h-4 w-4" />
              <span>{LANG_LABELS[lang].code}</span>
              <ChevronDown className="h-3.5 w-3.5" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-[160px] rounded-2xl">
              {SITE.languages.map((l) => (
                <DropdownMenuItem
                  key={l}
                  data-testid={`lang-option-${l}`}
                  onClick={() => handleLangSwitch(l)}
                  className={`cursor-pointer rounded-xl text-sm ${
                    l === lang ? "text-dental font-semibold" : "text-navy/80"
                  }`}
                >
                  <span className="mr-2">{LANG_LABELS[l].flag}</span>
                  {LANG_LABELS[l].label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* WhatsApp (secondary) */}
          <a
            href={WHATSAPP_LINK()}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="header-whatsapp-btn"
            className="hidden lg:inline-flex items-center gap-2 rounded-full border border-turquoise/30 px-4 py-2.5 text-sm font-semibold text-turquoise hover:bg-turquoise hover:text-white transition-colors"
          >
            <MessageCircle className="h-4 w-4" />
            {t.cta.whatsapp}
          </a>

          {/* Primary CTA */}
          <Link
            to={p("contact")}
            data-testid="header-cta-btn"
            className="hidden sm:inline-flex items-center gap-2 rounded-full bg-dental px-5 py-2.5 text-sm font-semibold text-white hover:bg-dental-dark transition-all hover:scale-[1.02] shadow-soft"
          >
            {t.cta.freePlan}
            <ArrowRight className="h-4 w-4" />
          </Link>

          {/* Mobile menu */}
          <Sheet>
            <SheetTrigger
              data-testid="mobile-menu-trigger"
              className="xl:hidden inline-flex h-11 w-11 items-center justify-center rounded-full border border-navy/15 text-navy hover:bg-mist transition-colors"
              aria-label={t.cta.menu}
            >
              <Menu className="h-5 w-5" />
            </SheetTrigger>
            <SheetContent
              side="right"
              className="w-[88vw] max-w-sm p-0 flex flex-col"
              data-testid="mobile-menu-panel"
            >
              <div className="border-b border-navy/10 p-6">
                <SheetTitle className="sr-only">{t.cta.menu}</SheetTitle>
                <Logo />
              </div>
              <nav className="flex-1 overflow-y-auto p-4" aria-label="Mobile">
                {navItems.map((item) => (
                  <SheetClose asChild key={item.to + item.label}>
                    <Link
                      to={item.to}
                      data-testid={`mobile-nav-link-${item.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                      className="flex items-center justify-between rounded-2xl px-4 py-3.5 text-base font-semibold text-navy hover:bg-mist transition-colors"
                    >
                      {item.label}
                      <ArrowRight className="h-4 w-4 text-navy/30" />
                    </Link>
                  </SheetClose>
                ))}
              </nav>
              <div className="border-t border-navy/10 p-4 space-y-3">
                <SheetClose asChild>
                  <Link
                    to={p("contact")}
                    data-testid="mobile-cta-btn"
                    className="flex w-full items-center justify-center gap-2 rounded-full bg-dental px-5 py-3.5 text-base font-semibold text-white hover:bg-dental-dark transition-colors"
                  >
                    {t.cta.freePlan}
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                </SheetClose>
                <a
                  href={WHATSAPP_LINK()}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="mobile-whatsapp-btn"
                  className="flex w-full items-center justify-center gap-2 rounded-full border-2 border-turquoise px-5 py-3.5 text-base font-semibold text-turquoise hover:bg-turquoise hover:text-white transition-colors"
                >
                  <MessageCircle className="h-4 w-4" />
                  {t.cta.whatsapp}
                </a>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
